/**
 * Parses a date string and returns a human-readable relative time string
 * (e.g., "today", "yesterday", "5 days ago", "2 weeks ago").
 *
 * @param dateString - The date string to parse
 * @param locale - Optional locale for date formatting (defaults to browser locale)
 * @returns A string representing the relative time
 */
export function parseRelativeDate(dateString: string): string {
  const date = new Date(dateString);

  // Check if the date is valid
  if (Number.isNaN(date.getTime())) {
    return "Invalid date";
  }

  const now = new Date();
  const diffTime = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    if (now.getDate() === date.getDate()) {
      return "today";
    } else {
      return "yesterday";
    }
  } else if (diffDays === 1) {
    return "yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
  }
}

/**
 * Parses a date and returns it as "dd-MM-yyyy" format.
 *
 * @param date - The date to format
 * @returns A string representing the date in "dd-MM-yyyy" format
 */
export function formatDateToString(date: Date): string | null {
  // Check if the date is valid
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
}
